/* eslint-disable react/prop-types */
import { Label, Spinner } from "flowbite-react"
import moment from "moment"
import { useRouter } from "next/router"
import React, { useMemo, useEffect, useRef } from "react"

import { RightIcon } from "@assets/icons"
import MonoInlineDatePicker from "@components/elements/DatePickerInline/MonoInlineDatePicker"
import MonoSelect from "@components/elements/Select/MonoSelect"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"
import { phpToMoment } from "@mono/essentials/utils/Common"

interface IWidgetsDatePickerComponentProps {
  isDefaultViewWidget: boolean
  datepicker?: string | undefined
  highlightDates: Date[]
  excludeDates: Date[]
  minDate: Date
  isSelectedDate: Date
  loading: boolean
  children?: React.ReactNode
  onChange: (date: Date | null, event?: React.SyntheticEvent<any, Event>) => void
  domainDetails: IDomain
  inline?: boolean
  popperClassName?: string
  containerClassName?: string
  showSpinner?: boolean
  onMonthChange?: (date: Date | null, event?: React.SyntheticEvent<any, Event>) => void
}

const WidgetsDatePicker = ({
  isDefaultViewWidget,
  datepicker,
  ...props
}: IWidgetsDatePickerComponentProps) => {
  const router = useRouter()

  const { nextStep } = router.query
  const isWidgetsOnIframeModal = Boolean(window !== window.parent) && nextStep === "true"
  const selectedItemRef = useRef<HTMLDivElement | null>(null)

  const dateOptions = useMemo(() => {
    return props.highlightDates.map((date) => ({
      value: moment(date).format("YYYY-MM-DD"),
      label: moment(date).format(phpToMoment(props?.domainDetails?.date_format || "Y-m-d")),
    }))
  }, [props.highlightDates, props?.domainDetails])

  // Handle dropdown date selection
  const handleDropdownChange = (selectedOption: SelectOptionType) => {
    if (selectedOption && props?.onChange) {
      const selectedDate = moment(selectedOption.value).toDate()
      props.onChange(selectedDate)
    }
  }

  // Find current selected value for dropdown
  const selectedDropdownValue = useMemo(() => {
    if (!props?.isSelectedDate) return null

    const selectedDateStr = moment(props.isSelectedDate).format("YYYY-MM-DD")
    return dateOptions.find((option) => option.value === selectedDateStr) || null
  }, [props?.isSelectedDate, dateOptions])

  // Scroll selected item into view when in iframe modal
  useEffect(() => {
    if (isWidgetsOnIframeModal && selectedItemRef.current && props.isSelectedDate) {
      const timeoutId = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
        selectedItemRef.current = null
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [isWidgetsOnIframeModal, props.isSelectedDate])

  // Render dropdown mode
  if (datepicker && datepicker === "dropdown" && !isDefaultViewWidget) {
    return (
      <div className={"w-full"} ref={selectedDropdownValue ? selectedItemRef : null}>
        <Label className={"text-neutral-900 text14Medium mb-2"}>
          Select Available Date & Time:
        </Label>
        <MonoSelect
          options={dateOptions}
          value={selectedDropdownValue}
          onValueChange={handleDropdownChange}
          placeholder={"Select Available Date"}
          className={"w-full bg-whiteBlack-100 !rounded-md"}
          menuPosition={"fixed"}
          menuPlacement={"auto"}
          isLoading={props?.loading}
          isSearchable={false}
          isFromWidget
          selectedValueColor={"text-neutral-1000"}
          components={{
            LoadingIndicator: () => <Spinner size={"sm"} />,
          }}
        />
      </div>
    )
  }

  if (datepicker && (datepicker === "list" || (datepicker === "dropdown" && isDefaultViewWidget))) {
    if (props.loading) {
      return (
        <div className={"flex justify-center items-center mt-2 min-w-[300px]"}>
          <Spinner size={"md"} />
        </div>
      )
    }

    return (
      <div
        className={`overflow-y-auto min-w-[300px] scrollbar overflow-x-hidden ${
          isWidgetsOnIframeModal ? "max-h-[300px]" : "max-h-[600px]"
        }`}
      >
        {props.highlightDates.map((date) => {
          const isSelected =
            moment(props.isSelectedDate).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD")

          return (
            <div
              ref={isSelected ? selectedItemRef : null}
              className={`flex flex-col gap-2 min-w-[300px] rounded-md border text-neutral-900 mr-2 mt-2 ${
                isSelected ? "border-primary-600" : "border-neutral-300"
              }`}
              key={date.toString()}
              role={"button"}
              tabIndex={0}
              onClick={() => {
                props.onChange(moment(date).toDate())
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  props.onChange(moment(date).toDate())
                }
              }}
            >
              <div
                className={
                  "flex justify-between items-center text14Medium text-neutral-90 rounded-md p-2 cursor-pointer"
                }
              >
                {moment(date).format(phpToMoment(props?.domainDetails?.date_format || "Y-m-d"))}
                <div>
                  <RightIcon width={16} height={16} className={"mr-2"} />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Default calendar mode
  return <MonoInlineDatePicker {...props} />
}

export default WidgetsDatePicker
